import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Reads the active, already rendered product image. Native variant and cart
 * components remain the owners of product state and purchase behaviour.
 * @typedef {Object} ZoomRefs
 * @property {HTMLButtonElement} zoomOpenButton
 * @property {HTMLDialogElement} zoomDialog
 * @property {HTMLButtonElement} zoomCloseButton
 * @property {HTMLButtonElement} zoomInButton
 * @property {HTMLButtonElement} zoomOutButton
 * @property {HTMLElement} zoomLevel
 * @property {HTMLElement} zoomViewport
 * @property {HTMLElement} zoomStage
 * @property {HTMLImageElement} zoomImage
 * @extends {Component<ZoomRefs>}
 */
export class EclipseImageZoom extends Component {
  requiredRefs = ['zoomOpenButton', 'zoomDialog', 'zoomCloseButton', 'zoomInButton', 'zoomOutButton', 'zoomLevel', 'zoomViewport', 'zoomStage', 'zoomImage'];
  #abort;
  #observer;
  #frame = 0;
  #gallery;
  #activeSlide;
  #scale = 1;
  #width = 1600;
  #height = 2000;
  #fallback = '';
  #drag;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#gallery = this.closest('media-gallery');
    if (!this.#gallery) return;
    const options = { signal: this.#abort.signal };
    this.#gallery.addEventListener(SlideshowSelectEvent.eventName, this.#selected, options);
    this.addEventListener('load', this.#loaded, { ...options, capture: true });
    this.addEventListener('error', this.#failed, { ...options, capture: true });
    this.addEventListener('cancel', this.#cancelled, { ...options, capture: true });
    this.addEventListener('close', this.#closed, { ...options, capture: true });
    this.addEventListener('pointerdown', this.#pointerDown, options);
    this.addEventListener('pointermove', this.#pointerMove, options);
    this.addEventListener('pointerup', this.#pointerEnd, options);
    this.addEventListener('pointercancel', this.#pointerEnd, options);
    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(this.#gallery);
    this.#observer.observe(this.refs.zoomViewport);
    const photo = this.#gallery.querySelector('slideshow-container');
    if (photo) this.#observer.observe(photo);
    this.#schedule();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#pointerEnd();
    if (this.refs.zoomDialog?.open) this.refs.zoomDialog.close();
    super.disconnectedCallback();
  }

  #selected = (event) => {
    const slide = event.detail?.slide;
    if (slide && this.#gallery?.contains(slide)) this.#activeSlide = slide;
    this.#schedule();
  };

  #activeImage() {
    const slide = this.#activeSlide?.isConnected && this.#gallery?.contains(this.#activeSlide) ? this.#activeSlide : this.#gallery?.querySelector('slideshow-slide[aria-hidden="false"]');
    if (!slide?.classList.contains('product-media-container--image')) return null;
    return slide.querySelector('.product-media img');
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#positionTrigger();
      if (this.refs.zoomDialog.open) this.#render();
    });
  };

  #positionTrigger() {
    const photo = this.#gallery?.querySelector('slideshow-container');
    const source = this.#activeImage();
    const button = this.refs.zoomOpenButton;
    if (!photo || !source || !this.#gallery) {
      button.hidden = true;
      return;
    }
    const frame = photo.getBoundingClientRect();
    const gallery = this.#gallery.getBoundingClientRect();
    button.hidden = frame.width <= 0 || frame.height <= 0;
    this.style.setProperty('--eclipse-zoom-left', `${Math.max(0, Math.round(frame.right - gallery.left - 56))}px`);
    this.style.setProperty('--eclipse-zoom-top', `${Math.max(0, Math.round(frame.bottom - gallery.top - 56))}px`);
  }

  /** Open the active photograph at its fitted size. */
  open(event) {
    event?.preventDefault();
    const source = this.#activeImage();
    const { zoomDialog, zoomImage, zoomCloseButton } = this.refs;
    if (!source || zoomDialog.open) return;
    const sourceUrl = source.getAttribute('data-max-resolution') || source.currentSrc || source.src;
    if (!sourceUrl) return;
    this.#width = Number(source.getAttribute('width')) || source.naturalWidth || 1600;
    this.#height = Number(source.getAttribute('height')) || source.naturalHeight || 2000;
    this.#fallback = source.currentSrc || source.src;
    this.#scale = 1;
    zoomImage.alt = source.alt;
    zoomImage.width = this.#width;
    zoomImage.height = this.#height;
    zoomImage.src = sourceUrl;
    zoomDialog.showModal();
    this.#render(false);
    zoomCloseButton.focus({ preventScroll: true });
    this.#schedule();
  }

  /** Native dialog close also handles keyboard focus containment and Escape. */
  close(event) {
    event?.preventDefault();
    if (this.refs.zoomDialog.open) this.refs.zoomDialog.close();
  }

  zoomIn(event) {
    event?.preventDefault();
    this.#scale = 2;
    this.#render();
  }

  zoomOut(event) {
    event?.preventDefault();
    this.#scale = 1;
    this.#render();
  }

  #render(preserveCenter = true) {
    const { zoomDialog, zoomViewport: viewport, zoomStage: stage, zoomImage: image } = this.refs;
    if (!zoomDialog.open || viewport.clientWidth <= 0 || viewport.clientHeight <= 0) return;
    const previousWidth = stage.offsetWidth || viewport.clientWidth;
    const previousHeight = stage.offsetHeight || viewport.clientHeight;
    const centerX = preserveCenter ? (viewport.scrollLeft + viewport.clientWidth / 2) / previousWidth : .5;
    const centerY = preserveCenter ? (viewport.scrollTop + viewport.clientHeight / 2) / previousHeight : .5;
    const fit = Math.min(Math.max(1, viewport.clientWidth - 32) / this.#width, Math.max(1, viewport.clientHeight - 32) / this.#height);
    const width = Math.round(this.#width * fit * this.#scale);
    const height = Math.round(this.#height * fit * this.#scale);
    const stageWidth = Math.max(viewport.clientWidth, width + 32);
    const stageHeight = Math.max(viewport.clientHeight, height + 32);
    stage.style.width = `${stageWidth}px`;
    stage.style.height = `${stageHeight}px`;
    image.style.width = `${width}px`;
    image.style.height = `${height}px`;
    viewport.toggleAttribute('data-zoomed', this.#scale > 1);
    viewport.scrollTo({ left: Math.max(0, centerX * stageWidth - viewport.clientWidth / 2), top: Math.max(0, centerY * stageHeight - viewport.clientHeight / 2), behavior: 'instant' });
    this.refs.zoomInButton.disabled = this.#scale === 2;
    this.refs.zoomOutButton.disabled = this.#scale === 1;
    this.refs.zoomLevel.textContent = `${this.#scale * 100}%`;
  }

  #loaded = (event) => {
    if (event.target !== this.refs.zoomImage) return;
    if (this.refs.zoomImage.naturalWidth && this.refs.zoomImage.naturalHeight) {
      this.#width = this.refs.zoomImage.naturalWidth;
      this.#height = this.refs.zoomImage.naturalHeight;
    }
    this.#schedule();
  };

  #failed = (event) => {
    const image = this.refs.zoomImage;
    if (event.target !== image || !this.#fallback || image.src === this.#fallback) return;
    image.src = this.#fallback;
  };

  #cancelled = (event) => {
    if (event.target !== this.refs.zoomDialog) return;
    event.preventDefault();
    this.close();
  };

  #closed = (event) => {
    if (event.target !== this.refs.zoomDialog) return;
    this.#pointerEnd();
    this.#scale = 1;
    if (this.isConnected && this.refs.zoomOpenButton.isConnected) this.refs.zoomOpenButton.focus({ preventScroll: true });
  };

  #pointerDown = (event) => {
    const viewport = this.refs.zoomViewport;
    if (event.pointerType !== 'mouse' || event.button !== 0 || this.#scale !== 2 || !viewport.contains(event.target)) return;
    event.preventDefault();
    this.#drag = { x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop, id: event.pointerId };
    viewport.setPointerCapture(event.pointerId);
    viewport.setAttribute('data-panning', '');
  };

  #pointerMove = (event) => {
    if (!this.#drag || event.pointerId !== this.#drag.id) return;
    const viewport = this.refs.zoomViewport;
    viewport.scrollLeft = this.#drag.left + this.#drag.x - event.clientX;
    viewport.scrollTop = this.#drag.top + this.#drag.y - event.clientY;
  };

  #pointerEnd = () => {
    const viewport = this.refs.zoomViewport;
    if (this.#drag && viewport?.hasPointerCapture(this.#drag.id)) viewport.releasePointerCapture(this.#drag.id);
    viewport?.removeAttribute('data-panning');
    this.#drag = undefined;
  };
}

if (!customElements.get('eclipse-image-zoom')) customElements.define('eclipse-image-zoom', EclipseImageZoom);
